import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { StretchHorizontal, Trash2, Plus, Pencil, X, Timer } from "lucide-react";

const EMPTY_EX = { name: "", muscle_group: "", description: "", image_url: "" };
const EMPTY_RT = { name: "", description: "", exercises: [] };

export default function CoachStretching() {
  const { t } = useLang();
  const [tab, setTab] = useState("routines");
  const [exercises, setExercises] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [exForm, setExForm] = useState(EMPTY_EX);
  const [exEditId, setExEditId] = useState(null);
  const [exFile, setExFile] = useState(null);
  const [rtForm, setRtForm] = useState(EMPTY_RT);
  const [rtEditId, setRtEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => {
    api.get("/stretch-exercises").then((r) => setExercises(r.data)).catch(() => {});
    api.get("/coach/stretch-routines").then((r) => setRoutines(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveExercise = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let id = exEditId;
      if (id) {
        await api.put(`/stretch-exercises/${id}`, exForm);
      } else {
        const r = await api.post("/stretch-exercises", exForm);
        id = r.data._id;
      }
      if (exFile) {
        const fd = new FormData();
        fd.append("file", exFile);
        await api.post(`/stretch-exercises/${id}/image`, fd);
      }
      toast.success(t("st.saved"));
      setExForm(EMPTY_EX); setExEditId(null); setExFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const editExercise = (ex) => {
    setExEditId(ex._id);
    setExForm({ name: ex.name, muscle_group: ex.muscle_group || "", description: ex.description || "", image_url: ex.image_url || "" });
    setExFile(null);
    setTab("exercises");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeExercise = async (id) => {
    try {
      await api.delete(`/stretch-exercises/${id}`);
      toast.success(t("st.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setRtItem = (i, field, value) => {
    const items = rtForm.exercises.map((it, idx) => (idx === i ? { ...it, [field]: value } : it));
    setRtForm({ ...rtForm, exercises: items });
  };

  const saveRoutine = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...rtForm, exercises: rtForm.exercises.filter((it) => it.exercise_id) };
      if (rtEditId) {
        await api.put(`/coach/stretch-routines/${rtEditId}`, payload);
      } else {
        await api.post("/coach/stretch-routines", payload);
      }
      toast.success(t("st.saved"));
      setRtForm(EMPTY_RT); setRtEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const editRoutine = (rt) => {
    setRtEditId(rt._id);
    setRtForm({ name: rt.name, description: rt.description || "", exercises: rt.exercises || [] });
    setTab("routines");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeRoutine = async (id) => {
    try {
      await api.delete(`/coach/stretch-routines/${id}`);
      toast.success(t("st.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exName = (id) => exercises.find((e) => e._id === id)?.name || "—";
  const rtTotal = (rt) => Math.round((rt.exercises || []).reduce((s, it) => s + (it.duration_sec || 0), 0) / 60);

  return (
    <Layout nav={nav}>
      <div className="max-w-3xl space-y-8" data-testid="coach-stretching-page">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <StretchHorizontal className="w-7 h-7 text-primary" /> {t("st.title")}
        </h1>

        <div className="flex gap-2" data-testid="stretching-tabs">
          <button data-testid="stretching-tab-routines" onClick={() => setTab("routines")}
            className={`kc-badge !px-4 !py-2 cursor-pointer ${tab === "routines" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("st.routines")}
          </button>
          <button data-testid="stretching-tab-exercises" onClick={() => setTab("exercises")}
            className={`kc-badge !px-4 !py-2 cursor-pointer ${tab === "exercises" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("st.exercises")}
          </button>
        </div>

        {tab === "exercises" && (
          <>
            <form onSubmit={saveExercise} className="kc-card p-5 space-y-4" data-testid="stretch-exercise-form">
              <div className="flex items-center justify-between">
                <h2 className="font-bold">{exEditId ? t("st.editExercise") : t("st.addExercise")}</h2>
                {exEditId && (
                  <button type="button" data-testid="stretch-exercise-cancel" onClick={() => { setExEditId(null); setExForm(EMPTY_EX); setExFile(null); }}
                    className="kc-btn-ghost !px-2.5"><X className="w-4 h-4" /></button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.name")}</label>
                  <input data-testid="stretch-exercise-name" required value={exForm.name}
                    onChange={(e) => setExForm({ ...exForm, name: e.target.value })} className="kc-input mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.muscleGroup")}</label>
                  <input data-testid="stretch-exercise-group" value={exForm.muscle_group}
                    onChange={(e) => setExForm({ ...exForm, muscle_group: e.target.value })} className="kc-input mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.desc")}</label>
                <textarea data-testid="stretch-exercise-desc" rows={2} value={exForm.description}
                  onChange={(e) => setExForm({ ...exForm, description: e.target.value })} className="kc-input mt-1" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.image")}</label>
                  <label className="mt-1 flex items-center gap-2 kc-input cursor-pointer text-sm text-muted-foreground" data-testid="stretch-exercise-image-upload">
                    <Plus className="w-4 h-4 shrink-0" />
                    <span className="truncate">{exFile ? exFile.name : "jpg / png / webp"}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      data-testid="stretch-exercise-image-input" onChange={(e) => setExFile(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.imageUrl")}</label>
                  <input data-testid="stretch-exercise-imageurl" type="url" placeholder="https://..." value={exForm.image_url}
                    onChange={(e) => setExForm({ ...exForm, image_url: e.target.value })} className="kc-input mt-1" />
                </div>
              </div>
              <button data-testid="stretch-exercise-submit" type="submit" disabled={saving || !exForm.name.trim()}
                className="kc-btn-primary inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> {saving ? t("common.loading") : (exEditId ? t("st.editExercise") : t("st.addExercise"))}
              </button>
            </form>

            <div className="space-y-3" data-testid="stretch-exercises-list">
              {exercises.length === 0 && <p className="text-muted-foreground text-sm">{t("st.emptyEx")}</p>}
              {exercises.map((ex) => (
                <div key={ex._id} className="kc-card p-4 flex items-start gap-4" data-testid={`stretch-exercise-card-${ex._id}`}>
                  {ex.image_url && <img src={ex.image_url} alt={ex.name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold">{ex.name}
                      {ex.muscle_group && <span className="kc-badge bg-secondary text-muted-foreground ml-2">{ex.muscle_group}</span>}
                    </p>
                    {ex.description && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{ex.description}</p>}
                  </div>
                  <button data-testid={`stretch-exercise-edit-${ex._id}`} onClick={() => editExercise(ex)} className="kc-btn-ghost !px-2.5 shrink-0">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`stretch-exercise-delete-${ex._id}`} onClick={() => removeExercise(ex._id)} className="kc-btn-ghost !px-2.5 shrink-0">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "routines" && (
          <>
            <form onSubmit={saveRoutine} className="kc-card p-5 space-y-4" data-testid="stretch-routine-form">
              <div className="flex items-center justify-between">
                <h2 className="font-bold">{rtEditId ? t("st.editRoutine") : t("st.addRoutine")}</h2>
                {rtEditId && (
                  <button type="button" data-testid="stretch-routine-cancel" onClick={() => { setRtEditId(null); setRtForm(EMPTY_RT); }}
                    className="kc-btn-ghost !px-2.5"><X className="w-4 h-4" /></button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.name")}</label>
                  <input data-testid="stretch-routine-name" required value={rtForm.name}
                    onChange={(e) => setRtForm({ ...rtForm, name: e.target.value })} className="kc-input mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("st.desc")}</label>
                  <input data-testid="stretch-routine-desc" value={rtForm.description}
                    onChange={(e) => setRtForm({ ...rtForm, description: e.target.value })} className="kc-input mt-1" />
                </div>
              </div>
              <div className="space-y-2" data-testid="stretch-routine-items">
                {rtForm.exercises.map((it, i) => (
                  <div key={i} className="flex flex-col sm:flex-row gap-2" data-testid={`stretch-routine-item-${i}`}>
                    <select data-testid={`stretch-routine-item-ex-${i}`} value={it.exercise_id}
                      onChange={(e) => setRtItem(i, "exercise_id", e.target.value)} className="kc-input !py-2 text-sm flex-1">
                      <option value="">{t("st.pickExercise")}</option>
                      {exercises.map((ex) => <option key={ex._id} value={ex._id}>{ex.name}</option>)}
                    </select>
                    <input data-testid={`stretch-routine-item-dur-${i}`} type="number" min="5" step="5" value={it.duration_sec}
                      onChange={(e) => setRtItem(i, "duration_sec", Number(e.target.value))}
                      className="kc-input !py-2 text-sm sm:w-24" placeholder={t("st.duration")} title={t("st.duration")} />
                    <input data-testid={`stretch-routine-item-notes-${i}`} value={it.notes}
                      onChange={(e) => setRtItem(i, "notes", e.target.value)}
                      className="kc-input !py-2 text-sm flex-1" placeholder={t("st.notes")} />
                    <button type="button" data-testid={`stretch-routine-item-remove-${i}`}
                      onClick={() => setRtForm({ ...rtForm, exercises: rtForm.exercises.filter((_, idx) => idx !== i) })}
                      className="kc-btn-ghost !p-2 text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" data-testid="stretch-routine-add-item"
                  onClick={() => setRtForm({ ...rtForm, exercises: [...rtForm.exercises, { exercise_id: "", duration_sec: 30, notes: "" }] })}
                  className="kc-btn-secondary !px-3 !py-2 text-xs inline-flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> {t("st.addToRoutine")}
                </button>
              </div>
              <button data-testid="stretch-routine-submit" type="submit"
                disabled={saving || !rtForm.name.trim() || rtForm.exercises.filter((it) => it.exercise_id).length === 0}
                className="kc-btn-primary inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> {saving ? t("common.loading") : (rtEditId ? t("st.editRoutine") : t("st.addRoutine"))}
              </button>
            </form>

            <div className="space-y-3" data-testid="stretch-routines-list">
              {routines.length === 0 && <p className="text-muted-foreground text-sm">{t("st.emptyRt")}</p>}
              {routines.map((rt) => (
                <div key={rt._id} className="kc-card p-4" data-testid={`stretch-routine-card-${rt._id}`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold">{rt.name}
                        <span className="ml-2 text-xs font-semibold text-muted-foreground inline-flex items-center gap-1">
                          <Timer className="w-3.5 h-3.5" /> ~{rtTotal(rt)} {t("st.min")}
                        </span>
                      </p>
                      {rt.description && <p className="text-sm text-muted-foreground mt-0.5">{rt.description}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {(rt.exercises || []).map((it) => exName(it.exercise_id)).join(" · ")}
                      </p>
                    </div>
                    <button data-testid={`stretch-routine-edit-${rt._id}`} onClick={() => editRoutine(rt)} className="kc-btn-ghost !px-2.5 shrink-0">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`stretch-routine-delete-${rt._id}`} onClick={() => removeRoutine(rt._id)} className="kc-btn-ghost !px-2.5 shrink-0">
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
